import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { EmailCard } from './email-card'
import { Email, EmailGroup } from '@/types/email'
import { EmailCategory, EmailAnalysis, getAnalysisLabels } from '@/lib/llm/types'
import { deleteEmail, getEmailsFromCFWorker, getEmailContentFromCFWorker } from '@/lib/client/email'
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { IconInbox, IconRefresh, IconLoader2, IconArrowsUpDown, IconFilter, IconX, IconPlus, IconSettings, IconTrash } from '@tabler/icons-react';
import type { UserColumn, CreateColumnInput, UpdateColumnInput } from '@/types/column';
import { columnsClient } from '@/lib/cloudflare/columns-client';
import { getColumnEmails, countMatchingEmails } from '@/lib/column-filters';
import { ColumnBuilder } from './column-builder';
import { ColumnPresets } from './column-presets';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { VirtualizedColumn } from './dnd/virtualized-column';
import { DraggableItem } from './dnd/draggable-item';
import { EmailModal } from './email-modal';
import { SenderActionModal } from './sender-action-modal';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { useEmailProcessing } from '@/hooks/useEmailProcessing';
import { SelectedEmail, SortOption, ColumnSortState, SORT_OPTIONS, DEFAULT_COLUMN_SORTS } from '@/types/email-list.types';
import { sortEmailGroups } from '@/utils/email-sorting';
import { useSyncStore } from '@/lib/store/sync.store';
import { useSearchStore } from '@/lib/store/search.store';
import { ColumnPlaceholder } from '@/components/onboarding-placeholder';
import { getStoredSessionId } from '@/lib/cloudflare/auth-client';
import { preferencesClient } from '@/lib/cloudflare/preferences-client';
import { toggleStar } from '@/lib/cloudflare/star-client';
import {
  getCachedEmails,
  setCachedEmails,
  clearEmailCache,
  deleteCachedEmails,
  mergeIncrementalSync,
  updateCachedEmail,
} from '@/lib/store/email-cache';

const isValidCategory = (category: string): category is EmailCategory => {
  return ['do_nothing', 'delete', 'archive', 'unsubscribe'].includes(category);
};

type HeaderSyncActionDetail = {
  action: 'refresh';
};

const isNotFoundError = (error: unknown): boolean => {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : '';
  const status = typeof error === 'object' && error !== null
    ? (error as { status?: unknown }).status
    : undefined;

  return (
    status === 404 ||
    message.toLowerCase().includes('requested entity was not found') ||
    message.toLowerCase().includes('not found')
  );
};

// Extract sender email from sender string like "Name <email@domain.com>" or "email@domain.com"
const extractSenderEmail = (sender: string): string => {
  const emailMatch = sender.match(/<([^>]+)>/) || sender.match(/([^\s]+@[^\s]+)/);
  return (emailMatch ? emailMatch[1] : sender).toLowerCase().trim();
};

const extractSenderDomainFromSender = (sender: string): string | null => {
  const senderEmail = extractSenderEmail(sender);
  const atIndex = senderEmail.indexOf('@');
  if (atIndex === -1) {
    return null;
  }

  const domain = senderEmail.substring(atIndex + 1).trim();
  return domain || null;
};

// Extract sender name for display and localStorage key (for backwards compatibility)
const extractSenderKey = (sender: string): string => {
  const senderName = sender.match(/^"?([^"<]+)"?\s*(?:<[^>]+>)?/)?.[1].trim() || sender;
  return senderName.toLowerCase();
};

const COLUMN_SORTS_STORAGE_KEY = 'winbox_column_sorts';
const AUTO_REFRESH_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes
const AUTO_REFRESH_STALE_SECONDS = 6 * 60 * 60; // 6 hours

// Priority order lookups - hoisted outside component to avoid recreation in sort comparisons
const PRIORITY_ORDER_DESC: Record<string, number> = { important: 0, normal: 1, low: 2 };
const PRIORITY_ORDER_ASC: Record<string, number> = { important: 2, normal: 1, low: 0 };

// Load column sorts from localStorage
const loadColumnSorts = (): ColumnSortState => {
  if (typeof window === 'undefined') return DEFAULT_COLUMN_SORTS;
  try {
    const stored = localStorage.getItem(COLUMN_SORTS_STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_COLUMN_SORTS, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_COLUMN_SORTS;
};

// Save column sorts to localStorage
const saveColumnSorts = (sorts: ColumnSortState) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(COLUMN_SORTS_STORAGE_KEY, JSON.stringify(sorts));
  } catch {
    // Ignore storage errors
  }
};

const getReceivedAtUnix = (email: Email): number => {
  if (typeof email.receivedAtUnix === 'number' && Number.isFinite(email.receivedAtUnix)) {
    return email.receivedAtUnix;
  }

  const parsed = new Date(email.receivedAt).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export function EmailList() {
  const [emails, setEmails] = useState<Email[]>([]);
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<SelectedEmail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [accountSwitchWarning, setAccountSwitchWarning] = useState<string | null>(null);

  // Column sorting state
  const [columnSorts, setColumnSorts] = useState<ColumnSortState>(loadColumnSorts);

  // Update sort for a specific column
  const updateColumnSort = useCallback((category: EmailCategory, sortBy: SortOption) => {
    setColumnSorts(prev => {
      const newSorts = { ...prev, [category]: sortBy };
      saveColumnSorts(newSorts);
      return newSorts;
    });
  }, []);

  // Column filter state
  const [showFilter, setShowFilter] = useState<Record<EmailCategory, boolean>>({
    'do_nothing': false,
    'delete': false,
    'archive': false,
    'unsubscribe': false,
  });

  const [columnFilters, setColumnFilters] = useState<Record<EmailCategory, string>>({
    'do_nothing': '',
    'delete': '',
    'archive': '',
    'unsubscribe': '',
  });

  const toggleColumnFilter = useCallback((category: EmailCategory) => {
    setShowFilter(prev => ({ ...prev, [category]: !prev[category] }));
  }, []);

  const updateColumnFilter = useCallback((category: EmailCategory, value: string) => {
    setColumnFilters(prev => ({ ...prev, [category]: value }));
  }, []);

  const clearColumnFilter = useCallback((category: EmailCategory) => {
    setColumnFilters(prev => ({ ...prev, [category]: '' }));
  }, []);

  // Custom column filter state
  const [customColumnShowFilter, setCustomColumnShowFilter] = useState<Record<string, boolean>>({});
  const [customColumnFilters, setCustomColumnFilters] = useState<Record<string, string>>({});

  const toggleCustomColumnFilter = useCallback((columnId: string) => {
    setCustomColumnShowFilter(prev => ({ ...prev, [columnId]: !prev[columnId] }));
  }, []);

  const updateCustomColumnFilter = useCallback((columnId: string, value: string) => {
    setCustomColumnFilters(prev => ({ ...prev, [columnId]: value }));
  }, []);

  const clearCustomColumnFilter = useCallback((columnId: string) => {
    setCustomColumnFilters(prev => ({ ...prev, [columnId]: '' }));
  }, []);

  // dnd-kit sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Track active dragging item for DragOverlay
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeEmail, setActiveEmail] = useState<Email | null>(null);

  const [senderActionModals, setSenderActionModals] = useState<{
    senders: Array<{
      sender: string;
      senderKey: string;
      emails: Email[];
    }>;
    currentIndex: number;
  }>({
    senders: [],
    currentIndex: 0
  });

  // Custom columns state
  const [customColumns, setCustomColumns] = useState<UserColumn[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(true);
  const [isColumnPresetsOpen, setIsColumnPresetsOpen] = useState(false);
  const [isColumnBuilderOpen, setIsColumnBuilderOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<UserColumn | null>(null);

  const { user, accessToken } = useCFAuth();
  const { hasCompletedInitialSync, isPolling, progress } = useSyncStore();
  const { globalSearch } = useSearchStore();
  const isInitialProcessing = isPolling && progress?.status === 'processing' && !hasCompletedInitialSync;

  // Show onboarding placeholders during initial sync when no emails yet
  const showOnboardingPlaceholders = isInitialProcessing && emails.length === 0;

  // Use our custom hooks
    const {
    processingStatus,
    handleProcessing,
    cancelProcessing
  } = useEmailProcessing({ setEmails });

  // Prefetch state
  const [hoveredEmailId, setHoveredEmailId] = useState<string | null>(null);
  const [prefetchedContent, setPrefetchedContent] = useState<Record<string, any>>({});

  useEffect(() => {
    setError(null);
    setSelectedEmail(null);
    setPrefetchedContent({});
    setHoveredEmailId(null);
    setPageToken(null);
    setEmails([]);
    setIsLoading(true);
    setAccountSwitchWarning(null);
  }, [user?.id]);

  const prefetchEmailContent = useCallback(async (emailId: string) => {
    if (!prefetchedContent[emailId] && accessToken) {
      try {
        const content = await getEmailContentFromCFWorker(emailId, accessToken);
        setPrefetchedContent(prev => ({
          ...prev,
          [emailId]: content
        }));
      } catch {
        // Silently fail on prefetch errors
      }
    }
  }, [prefetchedContent, accessToken]);

  // Helper to normalize emails with preferences
  const normalizeEmails = useCallback((rawEmails: Email[]): Email[] => {
    const preferences = JSON.parse(localStorage.getItem('senderPreferences') || '{}');

    return rawEmails.map((email: Email): Email => {
      const analysisState = email.analysisState || 'final';
      const senderName = email.sender.match(/^"?([^"<]+)"?\s*(?:<[^>]+>)?/)?.[1].trim() || email.sender;
      const senderKey = senderName.toLowerCase();

      const normalizedAiAnalysis: EmailAnalysis = {
        category: email.aiAnalysis?.category || 'do_nothing',
        priority: email.aiAnalysis?.priority || 'normal',
        reason: email.aiAnalysis?.reason || '',
        labels: getAnalysisLabels(email.aiAnalysis),
        confidence: email.aiAnalysis?.confidence ?? 1.0,
        action_needed: email.aiAnalysis?.action_needed ?? false,
        deadline: email.aiAnalysis?.deadline ?? null,
        subcategory: email.aiAnalysis?.subcategory,
        action_type: email.aiAnalysis?.action_type,
        retention_period: email.aiAnalysis?.retention_period,
        context: email.aiAnalysis?.context,
      };

      const finalCategory = analysisState === 'preview'
        ? 'do_nothing'
        : preferences[senderKey] || normalizedAiAnalysis.category;

      return {
        ...email,
        threadId: email.threadId || email.id,
        receivedAtUnix: typeof email.receivedAtUnix === 'number'
          ? email.receivedAtUnix
          : Math.floor(new Date(email.receivedAt).getTime() / 1000),
        gmailLabels: email.gmailLabels || [],
        analysisState,
        aiAnalysis: {
          ...normalizedAiAnalysis,
          category: finalCategory,
        },
      };
    });
  }, []);

  const finalizedEmailCount = useMemo(
    () => emails.filter((email) => email.analysisState !== 'preview').length,
    [emails]
  );

  const mergeHydratedEmails = useCallback((currentEmails: Email[], hydratedEmails: Email[]): Email[] => {
    const hydratedById = new Map(hydratedEmails.map((email) => [email.id, email]));
    const existingIds = new Set(currentEmails.map((email) => email.id));

    const mergedExisting = currentEmails.map((email) => {
      const hydrated = hydratedById.get(email.id);
      if (!hydrated) {
        return email;
      }

      return {
        ...email,
        ...hydrated,
        gmailLabels: hydrated.gmailLabels?.length ? hydrated.gmailLabels : email.gmailLabels || [],
        analysisState: hydrated.analysisState || 'final',
      };
    });

    const newHydratedEmails = hydratedEmails.filter((email) => !existingIds.has(email.id));

    return [...newHydratedEmails, ...mergedExisting].sort(
      (a, b) => getReceivedAtUnix(b) - getReceivedAtUnix(a)
    );
  }, []);

  const refreshFromGmail = useCallback(async (
    {
      silent = false,
      trigger = 'manual',
    }: { silent?: boolean; trigger?: 'manual' | 'auto' | 'header' } = {}
  ) => {
    if (!user || !accessToken || isRefreshing) return;

    setIsRefreshing(true);
    try {
      const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
      const sessionId = getStoredSessionId();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      };

      // Get knownIds from current state for efficient sync
      const knownIds = emails.map(e => e.id);
      const cached = await getCachedEmails();
      const cacheForCurrentUser = cached.lastSyncUserId === user.id;

      // Single efficient refresh call with knownIds
      const refreshResponse = await fetch(`${CF_WORKER_URL}/api/emails/sync/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          knownIds,
          since: cacheForCurrentUser ? cached.lastSyncAt : null,
          trigger,
        }),
      });
      const refreshData = await refreshResponse.json();

      if (!refreshResponse.ok) {
        console.warn('Gmail refresh failed:', refreshData.error?.message);
        if (!silent) {
          toast.error('Failed to refresh');
        }
        return;
      }

      const { updated, deletedIds, newEmails: newEmailsData, syncAt, summary } = refreshData.data || {};

      console.log(`[refresh] Status: ${summary?.statusUpdates || 0} updates, ${summary?.removed || 0} removed, ${summary?.newCount || 0} new`);

      // Apply changes to state
      setEmails(prevEmails => {
        let merged = [...prevEmails];

        // 1. Remove deleted emails
        if (deletedIds?.length > 0) {
          const deletedSet = new Set(deletedIds);
          merged = merged.filter(e => !deletedSet.has(e.id));
        }

        // 2. Update status for existing emails
        if (updated?.length > 0) {
          type StatusUpdate = { id: string; isRead: boolean; isStarred: boolean };
          const updateMap = new Map<string, StatusUpdate>(
            updated.map((u: StatusUpdate) => [u.id, u])
          );
          merged = merged.map(email => {
            const update = updateMap.get(email.id);
            if (update) {
              return {
                ...email,
                isRead: update.isRead,
                isStarred: update.isStarred,
              };
            }
            return email;
          });
        }

        // 3. Add new emails to the top
        if (newEmailsData?.length > 0) {
          const normalizedNew = normalizeEmails(newEmailsData as Email[]);
          merged = [...normalizedNew, ...merged];
        }

        return merged;
      });

      // Update IndexedDB cache
      if (syncAt) {
        await mergeIncrementalSync(
          newEmailsData || [],
          deletedIds || [],
          syncAt,
          user.id
        );
      }

      // Toast message
      const parts: string[] = [];
      if (summary?.newCount > 0) parts.push(`${summary.newCount} new`);
      if (summary?.statusUpdates > 0) parts.push(`${summary.statusUpdates} updated`);
      if (summary?.removed > 0) parts.push(`${summary.removed} removed`);

      if (parts.length > 0) {
        if (!silent) {
          toast.success(parts.join(', '));
        }
      } else {
        if (!silent) {
          toast.success('Already up to date');
        }
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      if (!silent) {
        toast.error('Failed to refresh emails');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [user, accessToken, isRefreshing, emails, normalizeEmails]);

  // Manual refresh handler - efficient Gmail sync
  const handleRefresh = useCallback(() => {
    void refreshFromGmail({ trigger: 'manual' });
  }, [refreshFromGmail]);

  const handleHeaderSyncAction = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<HeaderSyncActionDetail>;
    if (customEvent?.detail?.action !== 'refresh') return;
    void refreshFromGmail({ trigger: 'header' });
  }, [refreshFromGmail]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('winbox:sync-action', handleHeaderSyncAction);

    return () => {
      window.removeEventListener('winbox:sync-action', handleHeaderSyncAction);
    };
  }, [handleHeaderSyncAction]);

  // Fetch emails on mount - with IndexedDB caching
  useEffect(() => {
    let isMounted = true;

    const fetchEmails = async () => {
      if (!user || !accessToken) return;

      try {
        setError(null);
        setAccountSwitchWarning(null);
        const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
        const sessionId = getStoredSessionId();
        const cached = await getCachedEmails();
        let isDifferentAccountCache = false;

        if (cached.lastSyncUserId && cached.lastSyncUserId !== user.id) {
          await clearEmailCache();
          isDifferentAccountCache = true;
          if (isMounted) {
            setAccountSwitchWarning('Your session changed to a different Gmail account. Old cache was blocked and cleared. Refreshing fresh data now.');
          }
          console.log('Cleared cached emails because cache belongs to a different user');
        }

        // Step 1: Try to load from IndexedDB cache first (instant render)
        if (hasCompletedInitialSync || isDifferentAccountCache) {
          if (cached.emails.length > 0 && cached.lastSyncUserId === user.id && cached.lastSyncAt) {
            console.log(`Loaded ${cached.emails.length} emails from IndexedDB cache`);
            const categorizedEmails = normalizeEmails(cached.emails);
            if (isMounted) {
              setEmails(categorizedEmails);
              setIsLoading(false); // Show cached data immediately
              if (!isDifferentAccountCache) {
                setAccountSwitchWarning(null);
              }
            }

            // Step 2: Fetch incremental changes in background
            try {
              const syncResponse = await fetch(
                `${CF_WORKER_URL}/api/emails/sync?limit=50000&since=${cached.lastSyncAt}`,
                {
                  credentials: 'include',
                  headers: sessionId ? { 'X-Session-Id': sessionId } : {},
                }
              );
              const syncData = await syncResponse.json();

              if (syncResponse.ok && syncData.success) {
                const hasChanges = syncData.emails?.length > 0 || syncData.deletedIds?.length > 0;

                if (hasChanges && isMounted) {
                  console.log(`Incremental sync: ${syncData.emails?.length || 0} updated, ${syncData.deletedIds?.length || 0} deleted`);

                  // Merge changes into state
                  setEmails(prevEmails => {
                    // Remove deleted emails
                    const deletedSet = new Set(syncData.deletedIds || []);
                    let merged = prevEmails.filter(e => !deletedSet.has(e.id));

                    // Update/add modified emails
                    if (syncData.emails?.length > 0) {
                      const updatedMap = new Map<string, Email>(
                        (syncData.emails as Email[]).map((e: Email) => [e.id, e])
                      );
                      merged = merged.map(e => updatedMap.get(e.id) ?? e);
                      // Add new emails not in current state
                      const existingIds = new Set(merged.map(e => e.id));
                      const newEmails = (syncData.emails as Email[]).filter((e: Email) => !existingIds.has(e.id));
                      merged = [...normalizeEmails(newEmails), ...merged];
                    }

                    return normalizeEmails(merged);
                  });

                  // Update IndexedDB cache
                  await mergeIncrementalSync(
                    syncData.emails || [],
                    syncData.deletedIds || [],
                    syncData.syncAt,
                    user.id
                  );
                  if (isMounted) {
                    setAccountSwitchWarning(null);
                  }
                } else if (syncData.syncAt) {
                  // No changes, but update the sync timestamp
                  await mergeIncrementalSync([], [], syncData.syncAt, user.id);
                  if (isMounted) {
                    setAccountSwitchWarning(null);
                  }
                }
              }
            } catch (syncError) {
              console.warn('Incremental sync failed:', syncError);
            }

            return; // Done with cached + incremental path
          }
        }

        // Step 3: No cache - do full sync
        setIsLoading(true);

        if (hasCompletedInitialSync || isDifferentAccountCache) {
          try {
            const syncResponse = await fetch(`${CF_WORKER_URL}/api/emails/sync?limit=50000`, {
              credentials: 'include',
              headers: sessionId ? { 'X-Session-Id': sessionId } : {},
            });
            const syncData = await syncResponse.json();

            if (syncResponse.ok && syncData.success && syncData.emails?.length > 0) {
              const categorizedEmails = normalizeEmails(syncData.emails);
              if (isMounted) {
                setEmails(categorizedEmails);
                setPageToken(null);
                console.log(`Full sync: ${syncData.emails.length} emails from server`);

                // Cache to IndexedDB for next load
                await setCachedEmails(syncData.emails, syncData.syncAt, user.id);
                if (isMounted) {
                  setAccountSwitchWarning(null);
                }
              }
              return;
            }
          } catch (syncError) {
            console.warn('Full sync failed, falling back to on-demand:', syncError);
          }
        }

        // Fallback: On-demand email fetching with LLM analysis
        const data = await getEmailsFromCFWorker(accessToken, {
          batchSize: 50,
          skipAnalysis: !hasCompletedInitialSync,
        });
        console.log(`Loaded ${data.emails.length} emails from CF Worker (on-demand)`);

        if (isMounted && data) {
          const categorizedEmails = normalizeEmails(data.emails);
          setEmails(categorizedEmails);
          setPageToken(data.nextPageToken || null);
          setAccountSwitchWarning(null);
        }
      } catch (error) {
        if (isNotFoundError(error)) {
          if (isMounted) {
            try {
              await clearEmailCache();
            } catch (cacheError) {
              console.error('Failed to clear email cache during not found handling:', cacheError);
            }

            setEmails([]);
            setPageToken(null);
            setError(null);
            toast.error('Session changed. Your email list has been reset.');
          }
          return;
        }

        if (isMounted) {
          setError(error instanceof Error ? error.message : 'Failed to fetch emails');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchEmails();

    return () => {
      isMounted = false;
    };
  }, [user, accessToken, hasCompletedInitialSync, normalizeEmails]);

  // Auto-refresh against Gmail after app load when local sync is stale.
  // This prevents return visits from showing old data until manual refresh.
  useEffect(() => {
    if (!user || !accessToken) return;
    if (!hasCompletedInitialSync) return;
    if (isLoading || isRefreshing) return;
    if (isPolling && progress?.status === 'processing') return;

    const runAutoRefresh = async () => {
      try {
        const cooldownKey = `winbox:auto-refresh:${user.id}`;
        const nowMs = Date.now();
        const lastAutoRefreshMs = Number(sessionStorage.getItem(cooldownKey) || '0');
        if (Number.isFinite(lastAutoRefreshMs) && nowMs - lastAutoRefreshMs < AUTO_REFRESH_COOLDOWN_MS) {
          return;
        }

        const cached = await getCachedEmails();
        const cacheIsForUser = cached.lastSyncUserId === user.id;
        const nowSeconds = Math.floor(nowMs / 1000);
        const cacheSyncAt = cacheIsForUser ? (cached.lastSyncAt || 0) : 0;
        const serverRefreshAt = typeof user.lastGmailRefreshAt === 'number'
          ? user.lastGmailRefreshAt
          : 0;
        const freshestSyncAt = Math.max(cacheSyncAt, serverRefreshAt);
        const cacheAgeSeconds = freshestSyncAt > 0
          ? nowSeconds - freshestSyncAt
          : Number.POSITIVE_INFINITY;

        if (cacheAgeSeconds < AUTO_REFRESH_STALE_SECONDS) {
          return;
        }

        sessionStorage.setItem(cooldownKey, String(nowMs));
        console.log(`[refresh:auto] Running silent Gmail refresh (cache age: ${Math.round(cacheAgeSeconds)}s)`);
        await refreshFromGmail({ silent: true, trigger: 'auto' });
      } catch (error) {
        console.warn('[refresh:auto] Failed to run auto refresh:', error);
      }
    };

    void runAutoRefresh();
  }, [
    user?.id,
    user?.lastGmailRefreshAt,
    accessToken,
    hasCompletedInitialSync,
    isLoading,
    isRefreshing,
    isPolling,
    progress?.status,
    refreshFromGmail,
  ]);

  // During initial/background sync, hydrate newly processed emails (and labels) as batches complete.
  useEffect(() => {
    if (!user || !accessToken) return;
    if (!(isPolling && progress?.status === 'processing')) return;

    const processedCount = progress.processed || 0;
    if (processedCount === 0 || processedCount <= finalizedEmailCount) return;

    let isCancelled = false;

    const hydrateProcessedEmails = async () => {
      try {
        const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
        const sessionId = getStoredSessionId();

        const syncResponse = await fetch(`${CF_WORKER_URL}/api/emails/sync?limit=50000`, {
          credentials: 'include',
          headers: sessionId ? { 'X-Session-Id': sessionId } : {},
        });
        const syncData = await syncResponse.json();

        if (!syncResponse.ok || !syncData.success || isCancelled) return;

        const syncedEmails = (syncData.emails || []) as Email[];
        if (syncedEmails.length === 0) return;

        const normalized = normalizeEmails(syncedEmails);
        if (!isCancelled) {
          setEmails((currentEmails) => mergeHydratedEmails(currentEmails, normalized));
          setPageToken(null);
        }

        if (!isCancelled && syncData.syncAt) {
          await setCachedEmails(syncedEmails, syncData.syncAt, user.id);
        }
      } catch (syncError) {
        console.warn('Live sync hydration failed:', syncError);
      }
    };

    hydrateProcessedEmails();

    return () => {
      isCancelled = true;
    };
  }, [
    user,
    accessToken,
    isPolling,
    progress?.status,
    progress?.processed,
    finalizedEmailCount,
    mergeHydratedEmails,
    normalizeEmails,
  ]);

  // Fetch custom columns
  useEffect(() => {
    if (!user) {
      setIsLoadingColumns(false);
      return;
    }

    const fetchColumns = async () => {
      try {
        setIsLoadingColumns(true);
        const columns = await columnsClient.getColumns();
        setCustomColumns(columns);
      } catch (error) {
        console.error('Failed to fetch custom columns:', error);
      } finally {
        setIsLoadingColumns(false);
      }
    };

    fetchColumns();
  }, [user]);

  // Custom column handlers
  const handleCreateColumn = async (input: CreateColumnInput) => {
    try {
      const newColumn = await columnsClient.createColumn(input);
      setCustomColumns(prev => [...prev, newColumn]);
      toast.success(`Column "${input.name}" created`);
    } catch (error) {
      console.error('Failed to create column:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create column');
    }
  };

  const handleUpdateColumn = async (columnId: string, input: UpdateColumnInput) => {
    try {
      const updated = await columnsClient.updateColumn(columnId, input);
      setCustomColumns(prev => prev.map(c => c.id === columnId ? updated : c));
      toast.success('Column updated');
    } catch (error) {
      console.error('Failed to update column:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update column');
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    try {
      await columnsClient.deleteColumn(columnId);
      setCustomColumns(prev => prev.filter(c => c.id !== columnId));
      toast.success('Column deleted');
    } catch (error) {
      console.error('Failed to delete column:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete column');
    }
  };

  const handleSaveColumn = async (input: CreateColumnInput | UpdateColumnInput) => {
    if (editingColumn) {
      await handleUpdateColumn(editingColumn.id, input);
    } else {
      await handleCreateColumn(input as CreateColumnInput);
    }
    setEditingColumn(null);
  };

  // Load more emails
  const loadMoreEmails = async () => {
    if (!pageToken || isLoadingMore || !accessToken) return;

    try {
      setIsLoadingMore(true);

      const data = await getEmailsFromCFWorker(accessToken, {
        batchSize: 50,
        pageToken
      });

      const preferences = JSON.parse(localStorage.getItem('senderPreferences') || '{}');

      const categorizedEmails: Email[] = data.emails.map((email: Email): Email => {
        const senderName = email.sender.match(/^"?([^"<]+)"?\s*(?:<[^>]+>)?/)?.[1].trim() || email.sender;
        const senderKey = senderName.toLowerCase();

        // Build the normalized aiAnalysis with proper defaults
        const normalizedAiAnalysis: EmailAnalysis = {
          category: email.aiAnalysis?.category || 'do_nothing',
          priority: email.aiAnalysis?.priority || 'normal',
          reason: email.aiAnalysis?.reason || '',
          labels: getAnalysisLabels(email.aiAnalysis),
          confidence: email.aiAnalysis?.confidence ?? 1.0,
          action_needed: email.aiAnalysis?.action_needed ?? false,
          deadline: email.aiAnalysis?.deadline ?? null,
          subcategory: email.aiAnalysis?.subcategory,
          action_type: email.aiAnalysis?.action_type,
          retention_period: email.aiAnalysis?.retention_period,
          context: email.aiAnalysis?.context,
        };

        // Apply user preference if exists
        const finalCategory = preferences[senderKey] || normalizedAiAnalysis.category;

        return {
          ...email,
          aiAnalysis: {
            ...normalizedAiAnalysis,
            category: finalCategory,
          },
        };
      });

      // Add new emails to existing ones
      setEmails(prevEmails => {
        const existingIds = new Set(prevEmails.map(e => e.id));
        const uniqueNewEmails = categorizedEmails.filter((e: Email) => !existingIds.has(e.id));
        return [...prevEmails, ...uniqueNewEmails];
      });

      setPageToken(data.nextPageToken || null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load more emails');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Helper function to get valid category
  const getValidCategory = (category: string | undefined): EmailCategory => {
    if (category && (category === 'do_nothing' || category === 'delete' ||
      category === 'archive' || category === 'unsubscribe')) {
      return category;
    }
    return 'do_nothing';
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);
    // Find the email being dragged
    const email = emails.find(e => e.id === id);
    setActiveEmail(email || null);
  };

  // Helper function to save sender preference
  const saveSenderPreference = useCallback(async (
    senderEmail: string,
    category: EmailCategory,
    source: 'manual_move' | 'bulk_action' | 'sender_modal' | 'settings_page' = 'manual_move'
  ) => {
    try {
      // Save to localStorage for backwards compatibility
      const senderKey = extractSenderKey(senderEmail);
      const preferences = JSON.parse(localStorage.getItem('senderPreferences') || '{}');
      preferences[senderKey] = category;
      localStorage.setItem('senderPreferences', JSON.stringify(preferences));

      // Save to server (async, non-blocking)
      preferencesClient.upsertPreference({
        senderPattern: extractSenderEmail(senderEmail),
        preferredCategory: category,
        source,
      }).catch(err => {
        console.error('Failed to sync preference to server:', err);
      });
    } catch (err) {
      console.error('Failed to save preference:', err);
    }
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Reset active state
    setActiveId(null);
    setActiveEmail(null);

    if (!over) return;

    const draggableId = active.id as string;
    const destinationId = over.id as string;

    // Determine source category from the draggable's data or ID
    const sourceCategory = (active.data.current?.category as string) ||
      draggableId.match(/^group-[a-z0-9]+-([^-]+)$/)?.[1];

    if (sourceCategory === destinationId) return;

    // Custom columns are view-based filters, not action categories
    if (destinationId.startsWith('custom-')) {
      toast.info('Custom columns show emails based on filters. Change the email\'s label to include it here.', {
        duration: 4000,
      });
      return;
    }

    // Validate destination is a valid action category
    if (!isValidCategory(destinationId)) {
      return;
    }

    try {
      // Check if this is an email drag (email IDs don't start with 'group-')
      if (!draggableId.startsWith('group-')) {
        // Handle individual email drag - find the email to get sender
        const draggedEmail = emails.find(e => e.id === draggableId);

        setEmails(prevEmails => {
          return prevEmails.map(email => {
            if (email.id === draggableId) {
              return {
                ...email,
                analysisState: 'final',
                aiAnalysis: {
                  ...(email.aiAnalysis || {}),
                  category: destinationId as EmailCategory,
                  reason: email.aiAnalysis?.reason || 'Manual categorization',
                  priority: (email.aiAnalysis?.priority || 'normal') as 'important' | 'normal' | 'low',
                  action_needed: email.aiAnalysis?.action_needed ?? false,
                  deadline: email.aiAnalysis?.deadline ?? null,
                  labels: getAnalysisLabels(email.aiAnalysis).length > 0 ? getAnalysisLabels(email.aiAnalysis) : ['Manual'],
                  confidence: email.aiAnalysis?.confidence ?? 1.0,
                  subcategory: email.aiAnalysis?.subcategory,
                  action_type: email.aiAnalysis?.action_type,
                  retention_period: email.aiAnalysis?.retention_period,
                  context: email.aiAnalysis?.context
                } as EmailAnalysis
              } as Email;
            }
            return email;
          });
        });

        // Save preference for this sender
        if (draggedEmail) {
          saveSenderPreference(draggedEmail.sender, destinationId as EmailCategory);
        }
      }
    } catch {
      setError('Failed to move email. Please try again.');
    }
  };

  // Add isEmailGroup type guard
  const isEmailGroup = (email: Email | EmailGroup): email is EmailGroup => {
    return 'emails' in email;
  };

  const handleEmailClick = async (emailOrGroup: Email | EmailGroup, e: React.MouseEvent) => {
    e.preventDefault();

    const targetEmail = isEmailGroup(emailOrGroup) ? emailOrGroup.latestEmail : emailOrGroup;

    try {
      // Use prefetched content if available
      let content;
      if (prefetchedContent[targetEmail.id]) {
        content = prefetchedContent[targetEmail.id];
      } else if (accessToken) {
        content = await getEmailContentFromCFWorker(targetEmail.id, accessToken);
      } else {
        throw new Error('Access token required to fetch email content');
      }

      setSelectedEmail({
        ...targetEmail,
        isRead: true,
        content: content.content,
        mimeType: content.mimeType,
        headers: content.headers,
        attachments: content.attachments
      });

      // Update emails list to mark as read
      setEmails(prevEmails => prevEmails.map(e => {
        if (e.id === targetEmail.id) {
          return { ...e, isRead: true };
        }
        return e;
      }));

    } catch (error: any) {
      const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
      const isTargetNotFound = isNotFoundError(error) || message.toLowerCase().includes('requested entity was not found') || message.toLowerCase().includes('not found');

      if (isTargetNotFound) {
        setPrefetchedContent((prev) => {
          const next = { ...prev };
          delete next[targetEmail.id];
          return next;
        });
        setEmails((prevEmails) => prevEmails.filter((email) => email.id !== targetEmail.id));
        try {
          await deleteCachedEmails([targetEmail.id]);
        } catch (cacheError) {
          console.warn('Failed to remove stale email from cache:', cacheError);
        }
        setSelectedEmail(null);
        toast.error('Email was not found and has been removed from the current view.');

        try {
          const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
          const sessionId = getStoredSessionId();
          const remainingKnownIds = emails
            .filter((email) => email.id !== targetEmail.id)
            .map((email) => email.id);

          await fetch(`${CF_WORKER_URL}/api/emails/sync/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
            },
            body: JSON.stringify({
              knownIds: remainingKnownIds,
              trigger: 'manual',
            }),
          });

          const syncResponse = await fetch(`${CF_WORKER_URL}/api/emails/sync?limit=50000`, {
            credentials: 'include',
            headers: sessionId ? { 'X-Session-Id': sessionId } : {},
          });
          const syncData = await syncResponse.json();

            if (syncResponse.ok && syncData.success && Array.isArray(syncData.emails) && user?.id) {
              const normalized = normalizeEmails(syncData.emails as Email[]);
              setEmails(normalized);
              if (syncData.syncAt) {
                await setCachedEmails(syncData.emails, syncData.syncAt, user.id);
              }
            }
        } catch (reconcileError) {
          console.warn('Failed to refresh emails after not-found email:', reconcileError);
        }
        return;
      }

      if (error?.status === 401) {
        window.location.href = '/api/auth/signin';
        return;
      }
      setError(
        message || 'Failed to fetch email content'
      );
    }
  };

  const handleEmailAction = async (action: 'delete' | 'archive', email?: Email) => {
    const targetEmail = email || selectedEmail;
    if (!targetEmail) return;

    try {
      if (action === 'delete') {
        await deleteEmail(targetEmail.id);
      } else if (action === 'archive') {
        throw new Error('Archive functionality not implemented yet');
      }

      // Update the email list after successful action
      setEmails(prevEmails => prevEmails.filter(e => e.id !== targetEmail.id));
      if (selectedEmail?.id === targetEmail.id) {
        setSelectedEmail(null);
      }
    } catch (error: any) {
      if (error.status === 401) {
        window.location.href = '/api/auth/signin';
        return;
      }
      toast.error(`Failed to ${action} email`);
    }
  };

  const handleModalEmailAction = useCallback((emailId: string, _action: 'delete' | 'archive' | 'spam' | 'unsubscribe') => {
    setEmails(prevEmails => {
      if (_action !== 'unsubscribe') {
        return prevEmails.filter(email => email.id !== emailId);
      }

      const targetEmail = prevEmails.find(email => email.id === emailId);
      if (!targetEmail) {
        return prevEmails;
      }

      const targetSenderEmail = extractSenderEmail(targetEmail.sender);
      const targetSenderDomain = extractSenderDomainFromSender(targetEmail.sender);

      return prevEmails.filter(email => {
        const emailAddress = extractSenderEmail(email.sender);
        const emailDomain = extractSenderDomainFromSender(email.sender);

        if (emailAddress === targetSenderEmail) {
          return false;
        }

        if (targetSenderDomain && emailDomain && targetSenderDomain === emailDomain) {
          return false;
        }

        return email.id !== emailId;
      });
    });

    setSelectedEmail(prev => (prev?.id === emailId ? null : prev));
  }, []);

  const handleEmailDelete = async (email: Email) => {
    try {
      await deleteEmail(email.id);
      setEmails(prevEmails => prevEmails.filter(e => e.id !== email.id));
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null);
      }
    } catch (error: any) {
      if (error.status === 401) {
        window.location.href = '/api/auth/signin';
        return;
      }
      toast.error('Failed to delete email');
    }
  };

  // Handle moving an individual email to a different category
  const handleMoveEmail = useCallback((email: Email, newCategory: EmailCategory) => {
    setEmails(prevEmails =>
      prevEmails.map(e => {
        if (e.id === email.id) {
          return {
            ...e,
            aiAnalysis: {
              ...e.aiAnalysis,
              category: newCategory
            } as EmailAnalysis
          };
        }
        return e;
      })
    );

    // Save preference for this sender
    saveSenderPreference(email.sender, newCategory);

    const categoryLabels: Record<EmailCategory, string> = {
      'do_nothing': 'Inbox',
      'unsubscribe': 'Unsubscribe',
      'archive': 'Archive',
      'delete': 'Delete'
    };
    toast.success(`Moved to ${categoryLabels[newCategory]}`);
  }, [saveSenderPreference]);

  const handleBulkAction = async (category: 'delete' | 'archive' | 'unsubscribe') => {
    try {
      const emailsInCategory = emailsByCategory[category] || [];

      if (emailsInCategory.length === 0) {
        return;
      }

      handleProcessing(emailsInCategory, category);

    } catch (error) {
      setError(`Failed to process ${category} action: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleSenderPreference = async (action: 'delete' | 'archive' | 'do_nothing') => {
    if (senderActionModals.senders.length > 0) {
      const currentSender = senderActionModals.senders[senderActionModals.currentIndex];

      // Save preference using the unified helper (localStorage + server sync)
      saveSenderPreference(currentSender.sender, action, 'sender_modal');

      // Update any existing emails from this sender
      setEmails(prevEmails => {
        return prevEmails.map(email => {
          const emailSenderName = email.sender.match(/^"?([^"<]+)"?\s*(?:<[^>]+>)?/)?.[1].trim() || email.sender;
          const emailSenderKey = emailSenderName.toLowerCase();

          if (emailSenderKey === currentSender.senderKey) {
            return {
              ...email,
              aiAnalysis: email.aiAnalysis ? {
                ...email.aiAnalysis,
                category: action,
                reason: email.aiAnalysis.reason || 'User preference',
                priority: email.aiAnalysis.priority || 'normal'
              } : {
                category: action,
                reason: 'User preference',
                priority: 'normal'
              }
            } as Email;
          }
          return email;
        });
      });

      // Move to next sender
      if (senderActionModals.currentIndex < senderActionModals.senders.length - 1) {
        setSenderActionModals(prev => ({
          ...prev,
          currentIndex: prev.currentIndex + 1
        }));
      } else {
        setSenderActionModals({
          senders: [],
          currentIndex: 0
        });
      }

      // Process unsubscribe in the background
      if (currentSender.emails.length > 0) {
        handleProcessing(currentSender.emails, 'unsubscribe');
      }
    }
  };

  const handleModalClose = () => {
    setSenderActionModals({
      senders: [],
      currentIndex: 0
    });
  };

  const handleStarToggle = async (email: Email, starred: boolean) => {
    try {
      // Optimistically update UI
      setEmails(prevEmails => prevEmails.map(e =>
        e.id === email.id ? { ...e, isStarred: starred } : e
      ));
      setSelectedEmail(prev => prev && prev.id === email.id
        ? { ...prev, isStarred: starred }
        : prev
      );

      // Call API to update star status (syncs with Gmail)
      await toggleStar(email.id, starred);
    } catch (error) {
      // Revert on failure
      setEmails(prevEmails => prevEmails.map(e =>
        e.id === email.id ? { ...e, isStarred: !starred } : e
      ));
      setSelectedEmail(prev => prev && prev.id === email.id
        ? { ...prev, isStarred: !starred }
        : prev
      );
      toast.error('Failed to update star status');
    }
  };

  const handleAnalysisUpdate = (email: Email, analysis: EmailAnalysis) => {
    setEmails(prevEmails => prevEmails.map(e =>
      e.id === email.id ? { ...e, aiAnalysis: analysis, analysisState: 'final' } : e
    ));
    setSelectedEmail(prev => prev && prev.id === email.id
      ? { ...prev, aiAnalysis: analysis, analysisState: 'final' }
      : prev
    );
  };

  // Helper function to check if an email matches a search term
  const emailMatchesSearch = useCallback((email: Email, searchTerm: string): boolean => {
    const searchLower = searchTerm.toLowerCase();
    return (
      email.sender.toLowerCase().includes(searchLower) ||
      email.subject?.toLowerCase().includes(searchLower) ||
      email.snippet?.toLowerCase().includes(searchLower)
    );
  }, []);

  // Helper to group emails by threadId and return only the latest email per thread
  const groupEmailsByThread = useCallback((emailList: Email[]): Email[] => {
    const threadGroups = new Map<string, Email[]>();

    // Group emails by threadId
    emailList.forEach(email => {
      const threadId = email.threadId || email.id;
      const group = threadGroups.get(threadId);
      if (group) {
        group.push(email);
      } else {
        threadGroups.set(threadId, [email]);
      }
    });

    // For each thread, return the latest email with an updated threadCount
    return Array.from(threadGroups.values()).map((threadEmails) => {
      // Sort by date descending to get the latest email first
      threadEmails.sort((a, b) =>
        getReceivedAtUnix(b) - getReceivedAtUnix(a)
      );

      const latestEmail = threadEmails[0];
      // Update threadCount to consistently reflect the number of emails in this view
      return {
        ...latestEmail,
        threadCount: threadEmails.length
      };
    });
  }, []);

  // Group emails by category and apply filtering + sorting
  const emailsByCategory = useMemo(() => {
    const categoryEmails: Record<EmailCategory, Email[]> = {
      'do_nothing': [],
      'delete': [],
      'archive': [],
      'unsubscribe': []
    };

    // First, apply global search filter
    let filteredEmails = emails;
    if (globalSearch) {
      filteredEmails = emails.filter(email => emailMatchesSearch(email, globalSearch));
    }

    // Categorize into columns
    filteredEmails.forEach(email => {
      const validCategory = isInitialProcessing && email.analysisState === 'preview'
        ? 'do_nothing'
        : getValidCategory(email.aiAnalysis?.category);
      categoryEmails[validCategory].push(email);
    });

    // Apply per-column filters, thread grouping, and sorting
    (Object.keys(categoryEmails) as EmailCategory[]).forEach(category => {
      let categoryEmailList = categoryEmails[category];

      // Apply column filter
      const filterText = columnFilters[category];
      if (filterText) {
        categoryEmailList = categoryEmailList.filter(email => emailMatchesSearch(email, filterText));
      }

      // Group emails by thread - show only the latest email per thread
      categoryEmailList = groupEmailsByThread(categoryEmailList);

      // Apply sorting
      const sortOption = columnSorts[category];
      categoryEmailList.sort((a, b) => {
        switch (sortOption) {
          case 'date_desc':
            return getReceivedAtUnix(b) - getReceivedAtUnix(a);
          case 'date_asc':
            return getReceivedAtUnix(a) - getReceivedAtUnix(b);
          case 'sender_asc':
            return a.sender.localeCompare(b.sender);
          case 'sender_desc':
            return b.sender.localeCompare(a.sender);
          case 'priority_desc':
            return (PRIORITY_ORDER_DESC[a.aiAnalysis?.priority || 'normal'] || 1) -
                   (PRIORITY_ORDER_DESC[b.aiAnalysis?.priority || 'normal'] || 1);
          case 'priority_asc':
            return (PRIORITY_ORDER_ASC[a.aiAnalysis?.priority || 'normal'] || 1) -
                   (PRIORITY_ORDER_ASC[b.aiAnalysis?.priority || 'normal'] || 1);
          case 'unread_first':
            return (a.isRead ? 1 : 0) - (b.isRead ? 1 : 0);
          case 'attachments_first':
            return (b.hasAttachments ? 1 : 0) - (a.hasAttachments ? 1 : 0);
          default:
            return 0;
        }
      });

      categoryEmails[category] = categoryEmailList;
    });

    return categoryEmails;
  }, [emails, columnSorts, columnFilters, globalSearch, emailMatchesSearch, groupEmailsByThread, isInitialProcessing]);

  // Memoized emails filtered by globalSearch for custom columns
  const globalSearchFilteredEmails = useMemo(() => {
    if (!globalSearch) return emails;
    return emails.filter(email => emailMatchesSearch(email, globalSearch));
  }, [emails, globalSearch, emailMatchesSearch]);

  const getCategoryCount = (category: EmailCategory) => {
    return emailsByCategory[category]?.length || 0;
  };

  // Lazy render function for EmailCard - only called for visible items
  const renderEmailCard = useCallback((email: Email) => {
    return (
      <EmailCard
        email={email}
        isGroup={false}
        isExpanded={false}
        onToggleExpand={() => {}}
        onClick={(e, evt) => handleEmailClick(e as Email, evt)}
        onAction={handleEmailAction}
        onMoveEmail={handleMoveEmail}
        onLabelChange={(e, oldLabel, newLabel) => {
          setEmails(prevEmails => prevEmails.map(em =>
            em.id === e.id
              ? {
                  ...em,
                  aiAnalysis: em.aiAnalysis
                    ? {
                        ...em.aiAnalysis,
                        labels: em.aiAnalysis.labels?.map(l => l === oldLabel ? newLabel : l) || [newLabel]
                      }
                    : undefined
                }
              : em
          ));
        }}
        onAnalysisUpdate={(e, analysis) => {
          setEmails(prevEmails => prevEmails.map(em =>
            em.id === e.id
              ? { ...em, aiAnalysis: analysis }
              : em
          ));
        }}
        onStarToggle={handleStarToggle}
        onMouseEnter={() => setHoveredEmailId(email.id)}
      />
    );
  }, [handleEmailClick, handleEmailAction, handleMoveEmail, handleStarToggle]);

  // Helper to get email key
  const getEmailKey = useCallback((email: Email) => email.id, []);

  
  // Trigger prefetch on hover
  useEffect(() => {
    if (hoveredEmailId) {
      prefetchEmailContent(hoveredEmailId);
    }
  }, [hoveredEmailId, prefetchEmailContent]);

  // Empty state - but NOT during active sync (show kanban with placeholders instead)
  const isActiveSync = isPolling && progress?.status === 'processing';
  const accountSwitchNotice = accountSwitchWarning ? (
    <div className="mx-2 mt-2 rounded-lg border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/70 dark:bg-amber-950/30 dark:text-amber-200">
      {accountSwitchWarning}
    </div>
  ) : null;

  if (!isLoading && emails.length === 0 && !isActiveSync) {
    return (
      <div className="w-full">
        {accountSwitchNotice}
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <IconInbox className="w-12 h-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-medium">No Emails Found</h3>
              <p className="text-sm text-muted-foreground">
                We couldn&apos;t find any emails in your inbox. Try refreshing or checking your filters.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
            >
              <IconRefresh className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Loading skeleton - matches actual kanban layout
  // Skip skeleton during active sync to show kanban with placeholders instead
  if (isLoading && !isActiveSync) {
    const fixedColumns = [
      { name: 'Inbox', hasAction: false },
      { name: 'Unsubscribe', hasAction: true },
      { name: 'Archive', hasAction: true },
      { name: 'Delete', hasAction: false },
    ];

    // Include custom columns in skeleton if already loaded, otherwise show placeholders
    const customColumnSkeletons = isLoadingColumns
      ? [{ name: 'Loading...', isPlaceholder: true }]
      : customColumns.filter(c => c.isVisible).map(c => ({ name: c.name, isPlaceholder: false }));

      return (
        <div className="w-full">
          {accountSwitchNotice}
          <div className="h-[calc(100vh-3.5rem)] overflow-x-auto pt-1">
            <div className="inline-flex gap-3 px-2 min-w-full w-max h-full">
          {fixedColumns.map((col, i) => (
            <div key={col.name} className={cn("w-[300px] flex flex-col", i < 3 && "border-r border-border/10")}>
              <div className="px-2 py-0.5 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">{col.name} <span className="text-[10px]">(0)</span></span>
                {col.hasAction && <Skeleton className="h-3 w-16" />}
              </div>
              <div className="flex-1 overflow-hidden p-1">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                  <div key={j} className="border-b border-border/40 px-2 py-1.5">
                    <div className="flex items-center justify-between mb-0.5">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-2.5 w-12" />
                    </div>
                    <Skeleton className="h-3 w-full mb-0.5" />
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-2.5 w-3/4" />
                      <Skeleton className="h-4 w-12 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* Custom column skeletons */}
          {customColumnSkeletons.map((col, i) => (
            <div key={`custom-skeleton-${i}`} className="w-[300px] flex flex-col border-r border-border/10">
              <div className="px-2 py-0.5 flex items-center justify-between">
                {col.isPlaceholder ? (
                  <Skeleton className="h-3 w-20" />
                ) : (
                  <span className="text-[11px] text-muted-foreground">{col.name} <span className="text-[10px]">(0)</span></span>
                )}
                <div className="flex items-center gap-1">
                  <Skeleton className="h-2.5 w-2.5" />
                  <Skeleton className="h-2.5 w-2.5" />
                </div>
              </div>
              <div className="flex-1 overflow-hidden p-1">
                {[1, 2, 3, 4, 5, 6].map((j) => (
                  <div key={j} className="border-b border-border/40 px-2 py-1.5">
                    <div className="flex items-center justify-between mb-0.5">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-2.5 w-12" />
                    </div>
                    <Skeleton className="h-3 w-full mb-0.5" />
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-2.5 w-3/4" />
                      <Skeleton className="h-4 w-12 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* Add column button skeleton */}
          <div className="w-[300px] flex flex-col items-center justify-center">
            <Skeleton className="h-8 w-32 rounded-full" />
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
      <div className="w-full">
        {accountSwitchNotice}
        {error && (
          <div className="text-center p-4 text-red-600 mb-4">
            Error: {error}
          </div>
        )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToWindowEdges]}
        autoScroll={{
          threshold: { x: 0.15, y: 0.15 },
          acceleration: 15,
        }}
      >
        <div className="h-[calc(100vh-3.5rem)] overflow-x-auto pt-1">
          <div className="inline-flex gap-3 px-2 min-w-full w-max h-full">
            {/* Inbox Column */}
            <div className="w-[300px] flex flex-col border-r border-border/10">
              <div className="px-2 py-0.5 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Inbox <span className="text-[10px]">({getCategoryCount('do_nothing')})</span></span>
                <div className="flex items-center gap-1">
                  <button
                    className="text-[10px] text-muted-foreground/70 hover:text-foreground disabled:opacity-50"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title="Refresh emails"
                  >
                    <IconRefresh className={cn("h-2.5 w-2.5", isRefreshing && "animate-spin")} />
                  </button>
                  <button
                    className={cn(
                      "text-[10px] text-muted-foreground/70 hover:text-foreground",
                      columnFilters['do_nothing'] && "text-primary"
                    )}
                    onClick={() => toggleColumnFilter('do_nothing')}
                  >
                    <IconFilter className="h-2.5 w-2.5" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1">
                      <IconArrowsUpDown className="h-2.5 w-2.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuRadioGroup value={columnSorts['do_nothing']} onValueChange={(v) => updateColumnSort('do_nothing', v as SortOption)}>
                        {SORT_OPTIONS.map(option => (
                          <DropdownMenuRadioItem key={option.value} value={option.value} className="text-xs">
                            {option.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {showFilter['do_nothing'] && (
                <div className="px-2 pb-1">
                  <div className="relative">
                    <Input
                      placeholder="Filter..."
                      className="h-6 text-xs pr-6"
                      value={columnFilters['do_nothing']}
                      onChange={(e) => updateColumnFilter('do_nothing', e.target.value)}
                    />
                    {columnFilters['do_nothing'] && (
                      <button
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                        onClick={() => clearColumnFilter('do_nothing')}
                      >
                        <IconX className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <VirtualizedColumn
                  id="do_nothing"
                  data={emailsByCategory['do_nothing'] || []}
                  getItemKey={getEmailKey}
                  renderItem={renderEmailCard}
                  emptyMessage="No emails"
                  placeholder={showOnboardingPlaceholders ? <ColumnPlaceholder category="do_nothing" isVisible={true} /> : undefined}
                />
              </div>
            </div>

            {/* Unsubscribe Column */}
            <div className="w-[300px] flex flex-col border-r border-border/10">
              <div className="px-2 py-0.5 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Unsubscribe <span className="text-[10px]">({getCategoryCount('unsubscribe')})</span></span>
                <div className="flex items-center gap-1">
                  <button
                    className={cn(
                      "text-[10px] text-muted-foreground/70 hover:text-foreground",
                      columnFilters['unsubscribe'] && "text-primary"
                    )}
                    onClick={() => toggleColumnFilter('unsubscribe')}
                  >
                    <IconFilter className="h-2.5 w-2.5" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1">
                      <IconArrowsUpDown className="h-2.5 w-2.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuRadioGroup value={columnSorts['unsubscribe']} onValueChange={(v) => updateColumnSort('unsubscribe', v as SortOption)}>
                        {SORT_OPTIONS.map(option => (
                          <DropdownMenuRadioItem key={option.value} value={option.value} className="text-xs">
                            {option.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1 ml-1"
                    onClick={() => handleBulkAction('unsubscribe')}
                    disabled={!!processingStatus.action}
                  >
                    {processingStatus.action === 'unsubscribe' ? (
                      <>
                        <IconLoader2 className="h-2.5 w-2.5 animate-spin" />
                        {processingStatus.processed}/{processingStatus.total}
                      </>
                    ) : 'Unsubscribe All'}
                  </button>
                </div>
              </div>
              {showFilter['unsubscribe'] && (
                <div className="px-2 pb-1">
                  <div className="relative">
                    <Input
                      placeholder="Filter..."
                      className="h-6 text-xs pr-6"
                      value={columnFilters['unsubscribe']}
                      onChange={(e) => updateColumnFilter('unsubscribe', e.target.value)}
                    />
                    {columnFilters['unsubscribe'] && (
                      <button
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                        onClick={() => clearColumnFilter('unsubscribe')}
                      >
                        <IconX className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <VirtualizedColumn
                  id="unsubscribe"
                  data={emailsByCategory['unsubscribe'] || []}
                  getItemKey={getEmailKey}
                  renderItem={renderEmailCard}
                  emptyMessage="No emails"
                  placeholder={showOnboardingPlaceholders ? <ColumnPlaceholder category="unsubscribe" isVisible={true} /> : undefined}
                />
              </div>
            </div>

            {/* Archive Column */}
            <div className="w-[300px] flex flex-col border-r border-border/10">
              <div className="px-2 py-0.5 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Archive <span className="text-[10px]">({getCategoryCount('archive')})</span></span>
                <div className="flex items-center gap-1">
                  <button
                    className={cn(
                      "text-[10px] text-muted-foreground/70 hover:text-foreground",
                      columnFilters['archive'] && "text-primary"
                    )}
                    onClick={() => toggleColumnFilter('archive')}
                  >
                    <IconFilter className="h-2.5 w-2.5" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1">
                      <IconArrowsUpDown className="h-2.5 w-2.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuRadioGroup value={columnSorts['archive']} onValueChange={(v) => updateColumnSort('archive', v as SortOption)}>
                        {SORT_OPTIONS.map(option => (
                          <DropdownMenuRadioItem key={option.value} value={option.value} className="text-xs">
                            {option.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1 ml-1"
                    onClick={() => handleBulkAction('archive')}
                    disabled={!!processingStatus.action}
                  >
                    {processingStatus.action === 'archive' ? (
                      <>
                        <IconLoader2 className="h-2.5 w-2.5 animate-spin" />
                        {processingStatus.processed}/{processingStatus.total}
                      </>
                    ) : 'Archive All'}
                  </button>
                </div>
              </div>
              {showFilter['archive'] && (
                <div className="px-2 pb-1">
                  <div className="relative">
                    <Input
                      placeholder="Filter..."
                      className="h-6 text-xs pr-6"
                      value={columnFilters['archive']}
                      onChange={(e) => updateColumnFilter('archive', e.target.value)}
                    />
                    {columnFilters['archive'] && (
                      <button
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                        onClick={() => clearColumnFilter('archive')}
                      >
                        <IconX className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <VirtualizedColumn
                  id="archive"
                  data={emailsByCategory['archive'] || []}
                  getItemKey={getEmailKey}
                  renderItem={renderEmailCard}
                  emptyMessage="No emails"
                  placeholder={showOnboardingPlaceholders ? <ColumnPlaceholder category="archive" isVisible={true} /> : undefined}
                />
              </div>
            </div>

            {/* Delete Column */}
            <div className="w-[300px] flex flex-col">
              <div className="px-2 py-0.5 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Delete <span className="text-[10px]">({getCategoryCount('delete')})</span></span>
                <div className="flex items-center gap-1">
                  <button
                    className={cn(
                      "text-[10px] text-muted-foreground/70 hover:text-foreground",
                      columnFilters['delete'] && "text-primary"
                    )}
                    onClick={() => toggleColumnFilter('delete')}
                  >
                    <IconFilter className="h-2.5 w-2.5" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1">
                      <IconArrowsUpDown className="h-2.5 w-2.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuRadioGroup value={columnSorts['delete']} onValueChange={(v) => updateColumnSort('delete', v as SortOption)}>
                        {SORT_OPTIONS.map(option => (
                          <DropdownMenuRadioItem key={option.value} value={option.value} className="text-xs">
                            {option.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1 ml-1"
                    onClick={() => handleBulkAction('delete')}
                    disabled={!!processingStatus.action}
                  >
                    {processingStatus.action === 'delete' ? (
                      <>
                        <IconLoader2 className="h-2.5 w-2.5 animate-spin" />
                        {processingStatus.processed}/{processingStatus.total}
                      </>
                    ) : 'Delete All'}
                  </button>
                </div>
              </div>
              {showFilter['delete'] && (
                <div className="px-2 pb-1">
                  <div className="relative">
                    <Input
                      placeholder="Filter..."
                      className="h-6 text-xs pr-6"
                      value={columnFilters['delete']}
                      onChange={(e) => updateColumnFilter('delete', e.target.value)}
                    />
                    {columnFilters['delete'] && (
                      <button
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                        onClick={() => clearColumnFilter('delete')}
                      >
                        <IconX className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <VirtualizedColumn
                  id="delete"
                  data={emailsByCategory['delete'] || []}
                  getItemKey={getEmailKey}
                  renderItem={renderEmailCard}
                  emptyMessage="No emails"
                  placeholder={showOnboardingPlaceholders ? <ColumnPlaceholder category="delete" isVisible={true} /> : undefined}
                />
              </div>
            </div>

            {/* Custom Columns */}
            {customColumns.filter(col => col.isVisible).map(column => {
              const allColumnEmails = getColumnEmails(globalSearchFilteredEmails, column);
              // Apply custom column filter
              const filterText = customColumnFilters[column.id] || '';
              let filteredColumnEmails = filterText
                ? allColumnEmails.filter(email => emailMatchesSearch(email, filterText))
                : allColumnEmails;
              // Group by thread - show only latest email per thread
              const columnEmails = groupEmailsByThread(filteredColumnEmails);
              return (
                <div key={column.id} className="w-[300px] flex flex-col border-r border-border/10">
                  <div className="px-2 py-0.5 flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                      {column.name} <span className="text-[10px]">({columnEmails.length})</span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        className={cn(
                          "text-[10px] text-muted-foreground/70 hover:text-foreground",
                          customColumnFilters[column.id] && "text-primary"
                        )}
                        onClick={() => toggleCustomColumnFilter(column.id)}
                      >
                        <IconFilter className="h-2.5 w-2.5" />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger className="text-[10px] text-muted-foreground/70 hover:text-foreground flex items-center gap-1">
                          <IconArrowsUpDown className="h-2.5 w-2.5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuRadioGroup
                            value={`${column.sortBy}_${column.sortDirection}`}
                            onValueChange={(v) => {
                              const [sortBy, sortDirection] = v.split('_') as ['date' | 'sender' | 'subject', 'asc' | 'desc'];
                              handleUpdateColumn(column.id, { sortBy, sortDirection });
                            }}
                          >
                            <DropdownMenuRadioItem value="date_desc" className="text-xs">Newest First</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="date_asc" className="text-xs">Oldest First</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="sender_asc" className="text-xs">Sender A-Z</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="sender_desc" className="text-xs">Sender Z-A</DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <DropdownMenu>
                        <DropdownMenuTrigger className="text-[10px] text-muted-foreground/70 hover:text-foreground">
                          <IconSettings className="h-2.5 w-2.5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-32">
                          <button
                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded-sm"
                            onClick={() => {
                              setEditingColumn(column);
                              setIsColumnBuilderOpen(true);
                            }}
                          >
                            Edit Column
                          </button>
                          <button
                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded-sm text-destructive"
                            onClick={() => handleDeleteColumn(column.id)}
                          >
                            Delete Column
                          </button>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {customColumnShowFilter[column.id] && (
                    <div className="px-2 pb-1">
                      <div className="relative">
                        <Input
                          placeholder="Filter..."
                          className="h-6 text-xs pr-6"
                          value={customColumnFilters[column.id] || ''}
                          onChange={(e) => updateCustomColumnFilter(column.id, e.target.value)}
                        />
                        {customColumnFilters[column.id] && (
                          <button
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                            onClick={() => clearCustomColumnFilter(column.id)}
                          >
                            <IconX className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex-1 overflow-hidden">
                    <VirtualizedColumn
                      id={`custom-${column.id}`}
                      data={columnEmails}
                      getItemKey={getEmailKey}
                      renderItem={renderEmailCard}
                      emptyMessage="No matching emails"
                    />
                  </div>
                </div>
              );
            })}

            {/* Add Column Button */}
            <div className="w-[300px] flex flex-col items-center justify-center">
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground hover:bg-muted/30 transition-colors"
                onClick={() => setIsColumnPresetsOpen(true)}
              >
                <IconPlus className="h-4 w-4" />
                <span className="text-sm">Add column</span>
              </button>
            </div>
          </div>
        </div>

        {/* Drag overlay */}
        <DragOverlay
          dropAnimation={{
            duration: 250,
            easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
          }}
          modifiers={[restrictToWindowEdges]}
        >
          {activeId && activeEmail ? (
            <div
              className="cursor-grabbing"
              style={{
                opacity: 0.92,
                transform: 'rotate(2deg) scale(1.02)',
                filter: 'drop-shadow(0 12px 20px rgba(0,0,0,0.15))',
              }}
            >
              <EmailCard
                email={activeEmail}
                isGroup={false}
                isExpanded={false}
                onToggleExpand={() => {}}
                onClick={() => {}}
                onAction={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Load More Button */}
      {pageToken && (
        <div className="flex justify-center py-4">
          <Button
            variant="outline"
            onClick={loadMoreEmails}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent mr-2" />
                Loading...
              </>
            ) : (
              'Load More Emails'
            )}
          </Button>
        </div>
      )}

      <EmailModal
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        email={selectedEmail}
        onEmailRead={(emailId) => {
          setEmails(prevEmails => prevEmails.map(email =>
            email.id === emailId ? { ...email, isRead: true } : email
          ));
          setSelectedEmail(prev => prev ? { ...prev, isRead: true } : null);
        }}
        onLabelChange={(emailId, oldLabel, newLabel) => {
          // Update the email in the list
          setEmails(prevEmails => prevEmails.map(email =>
            email.id === emailId
              ? {
                  ...email,
                  aiAnalysis: email.aiAnalysis
                    ? {
                        ...email.aiAnalysis,
                        labels: email.aiAnalysis.labels?.map(l => l === oldLabel ? newLabel : l) || [newLabel]
                      }
                    : undefined
                }
              : email
          ));
          // Update the selected email
          setSelectedEmail(prev => prev && prev.id === emailId
            ? {
                ...prev,
                aiAnalysis: prev.aiAnalysis
                  ? {
                      ...prev.aiAnalysis,
                      labels: prev.aiAnalysis.labels?.map(l => l === oldLabel ? newLabel : l) || [newLabel]
                    }
                  : undefined
              }
            : prev
          );
        }}
        onStarToggle={(emailId, starred) => {
          const email = emails.find(e => e.id === emailId);
          if (email) {
            handleStarToggle(email, starred);
          }
        }}
        onAnalysisUpdate={(emailId, analysis) => {
          const email = emails.find(e => e.id === emailId);
          if (email) {
            handleAnalysisUpdate(email, analysis);
          }
        }}
        onEmailAction={handleModalEmailAction}
      />

      <SenderActionModal
        isOpen={senderActionModals.senders.length > 0}
        onClose={handleModalClose}
        sender={senderActionModals.senders[senderActionModals.currentIndex]?.sender || ''}
        onAction={handleSenderPreference}
        totalSenders={senderActionModals.senders.length}
        currentSender={senderActionModals.currentIndex + 1}
      />

      <ColumnBuilder
        isOpen={isColumnBuilderOpen}
        onClose={() => {
          setIsColumnBuilderOpen(false);
          setEditingColumn(null);
        }}
        onSave={handleSaveColumn}
        existingColumn={editingColumn || undefined}
        matchCount={editingColumn ? getColumnEmails(emails, editingColumn).length : undefined}
      />

      <ColumnPresets
        isOpen={isColumnPresetsOpen}
        onClose={() => setIsColumnPresetsOpen(false)}
        onSelectPreset={async (input) => {
          await handleCreateColumn(input);
        }}
        onSelectCustom={() => {
          setEditingColumn(null);
          setIsColumnBuilderOpen(true);
        }}
      />
    </div>
  );
}

export default EmailList;
